import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, Loader2, Sparkles, Camera, Sun, Users, Check, Clock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { toast } from '@/components/ui/sonner';
import { Reveal } from '@/components/Reveal';
import { TextReveal } from '@/components/TextReveal';
import { SEO } from '@/components/SEO';
import { getServices, createBlueprint } from '@/lib/api';
import { SITE_IMAGES } from '@/lib/siteImages';
import { AI_FEATURES_ENABLED } from '@/lib/featureFlags';
import { PLANNER_PAGE } from '@/constants/testIds';

const TIME_OPTIONS = [
  { value: 'golden hour', label: 'Golden Hour' },
  { value: 'morning', label: 'Morning' },
  { value: 'midday', label: 'Midday' },
  { value: 'evening', label: 'Evening / Night' },
  { value: 'flexible', label: 'Flexible' },
];

const AESTHETIC_OPTIONS = ['Cinematic', 'Editorial', 'Candid', 'Moody', 'Bright & Airy', 'Classic', 'Bold & Colorful', 'Documentary'];

const USE_OPTIONS = ['Instagram', 'LinkedIn', 'Prints', 'Announcements', 'Website / Brand', 'Family & Personal'];

const STEPS = [
  { key: 'occasion', label: 'The Occasion', icon: Camera },
  { key: 'look', label: 'The Look', icon: Sun },
  { key: 'story', label: 'The Story', icon: Sparkles },
  { key: 'you', label: 'About You', icon: Users },
];

const EMPTY = {
  service_slug: '',
  occasion: '',
  session_date: '',
  time_of_day: 'golden hour',
  people_count: 1,
  aesthetic: [],
  location_preference: '',
  outfit_notes: '',
  intended_use: [],
  must_have_shots: '',
  additional_notes: '',
  name: '',
  email: '',
  phone: '',
};

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const fieldClass =
  'mt-1.5 bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] placeholder:text-[hsl(var(--lxra-muted))]/60 focus-visible:ring-[hsl(var(--lxra-gold))]';
const labelClass = 'text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]';
const errorClass = 'mt-1.5 text-xs text-[hsl(8,55%,58%)]';

const Pill = ({ selected, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    data-testid={testId}
    className={`inline-flex items-center gap-1.5 h-10 px-4 rounded-full border text-xs uppercase tracking-[0.08em] transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--lxra-gold))] ${
      selected
        ? 'border-[hsl(var(--lxra-gold))] bg-[hsl(var(--lxra-gold))]/15 text-[hsl(var(--lxra-gold-hover))]'
        : 'border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))]/60 hover:text-[hsl(var(--lxra-gold))]'
    }`}
  >
    {selected && <Check className="h-3.5 w-3.5" />}
    {children}
  </button>
);

function validateStep(step, form) {
  const errors = {};
  if (step === 0) {
    if (!form.occasion.trim()) errors.occasion = 'Tell us what we are celebrating or creating.';
    if (!form.people_count || form.people_count < 1) errors.people_count = 'At least one person.';
  }
  if (step === 3) {
    if (!form.name.trim()) errors.name = 'Please enter your name.';
    if (!form.email.trim()) errors.email = 'Please enter your email.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errors.email = 'Please enter a valid email address.';
  }
  return errors;
}

export default function SessionPlanner() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [services, setServices] = useState([]);
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [form, setForm] = useState(() => ({ ...EMPTY, service_slug: searchParams.get('service') || '' }));
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getServices().then(setServices).catch(() => setServices([]));
  }, []);

  const selectedService = useMemo(() => services.find((s) => s.slug === form.service_slug), [services, form.service_slug]);

  const set = (field) => (value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };
  const setFromEvent = (field) => (e) => set(field)(e.target.value);
  const toggleIn = (field, value) => () =>
    setForm((prev) => ({
      ...prev,
      [field]: prev[field].includes(value) ? prev[field].filter((v) => v !== value) : [...prev[field], value],
    }));

  const goNext = () => {
    const v = validateStep(step, form);
    setErrors(v);
    if (Object.keys(v).length) return;
    setDirection(1);
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const goBack = () => {
    setDirection(-1);
    setErrors({});
    setStep((s) => Math.max(s - 1, 0));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const v = validateStep(3, form);
    setErrors(v);
    if (Object.keys(v).length) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        service_slug: form.service_slug || null,
        people_count: Number(form.people_count) || 1,
      };
      const res = await createBlueprint(payload);
      toast.success('Your Creative Blueprint is being composed.');
      navigate(`/plan/${res.blueprint_id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Something went wrong. Please try again or contact the studio.';
      setErrors({ submit: msg });
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const variants = {
    enter: (dir) => ({ opacity: 0, x: dir > 0 ? 24 : -24 }),
    center: { opacity: 1, x: 0 },
    exit: (dir) => ({ opacity: 0, x: dir > 0 ? -24 : 24 }),
  };

  return (
    <div className="pt-16" data-testid={PLANNER_PAGE.root}>
      <SEO
        title="Plan Your Session"
        description="LXRA Intelligence — a Creative Blueprint experience for your session — is coming soon. Reach out directly in the meantime and we'll help you plan it."
      />
      <section className="grid grid-cols-1 lg:grid-cols-5 min-h-[calc(100vh-4rem)]">
        {/* EDITORIAL SIDE */}
        <div className="relative lg:col-span-2 bg-[#0F0F10] overflow-hidden lg:sticky lg:top-16 lg:h-[calc(100vh-4rem)]">
          <img src={SITE_IMAGES.aboutHero} alt="" className="absolute inset-0 h-full w-full object-cover opacity-50" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/60 to-[#0A0A0A]/30" />
          <div className="relative z-10 flex h-full flex-col justify-end px-6 sm:px-10 lg:px-12 py-14 lg:pt-16 lg:pb-32">
            <Reveal>
              <p className="text-xs tracking-[0.22em] uppercase text-[hsl(var(--lxra-gold))] mb-3">LXRA Intelligence</p>
              <h1 className="font-display text-4xl sm:text-5xl tracking-[0.03em] leading-[0.98] text-[hsl(var(--lxra-fg))]">
                <TextReveal text="PLAN YOUR" />
                <br />
                <TextReveal text="SESSION." delay={0.08} />
              </h1>
              <div className="mt-5 h-[2px] w-16 bg-[hsl(var(--lxra-gold))]" />
              <p className="mt-6 max-w-sm text-sm sm:text-base text-[hsl(var(--lxra-muted))] leading-relaxed">
                Photography captures reality. Technology expands possibility. Story gives it meaning.
              </p>
              <p className="mt-4 max-w-sm text-sm text-[hsl(var(--lxra-muted))]/80 leading-relaxed hidden min-[900px]:block lg:hidden xl:block">
                Answer a few questions and we&apos;ll compose a Creative Blueprint — direction, shot list, lighting, and locations
                across the DMV. Your photographer reviews every detail before the shoot.
              </p>
            </Reveal>
          </div>
        </div>

        {!AI_FEATURES_ENABLED ? (
          <div className="lg:col-span-3 flex items-center bg-[#0A0A0A] px-4 sm:px-8 lg:px-14 py-14 lg:py-20" data-testid={PLANNER_PAGE.comingSoon}>
            <Reveal className="w-full max-w-xl">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--lxra-gold))]/30 bg-[hsl(var(--lxra-gold))]/10 px-3 py-1 text-[11px] uppercase tracking-[0.14em] text-[hsl(var(--lxra-gold))]">
                <Clock className="h-3.5 w-3.5" /> Coming Soon
              </span>
              <h2 className="font-display text-3xl sm:text-4xl tracking-[0.03em] text-[hsl(var(--lxra-fg))] mt-4">
                LXRA INTELLIGENCE IS ON ITS WAY.
              </h2>
              <p className="mt-5 text-sm sm:text-base text-[hsl(var(--lxra-muted))] leading-relaxed max-w-md">
                We&apos;re putting the finishing touches on the Creative Blueprint experience — a few questions, and
                we compose your session&apos;s direction, shot list, lighting, and DMV locations before you even arrive.
              </p>
              <p className="mt-4 text-sm sm:text-base text-[hsl(var(--lxra-muted))] leading-relaxed max-w-md">
                In the meantime, tell us about your session directly and we&apos;ll help you plan it by hand — just as
                thoughtfully, just a little less automated.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/contact"
                  data-testid={PLANNER_PAGE.comingSoonContactLink}
                  className="inline-flex items-center h-12 px-7 rounded-xl bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] hover:-translate-y-0.5 transition-transform duration-200 text-xs uppercase tracking-[0.1em] font-semibold"
                >
                  Tell Us About Your Session
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
                <Link
                  to="/services"
                  className="inline-flex items-center h-12 px-6 rounded-xl border border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))] transition-colors duration-200 text-xs uppercase tracking-[0.1em] font-medium"
                >
                  Browse Services
                </Link>
              </div>
            </Reveal>
          </div>
        ) : (
        <div className="lg:col-span-3 flex items-start bg-[#0A0A0A] px-4 sm:px-8 lg:px-14 py-14 lg:py-20">
          <div className="w-full max-w-xl">
            {/* Step indicator */}
            <ol className="flex items-center gap-2 sm:gap-3 mb-10" data-testid={PLANNER_PAGE.stepIndicator}>
              {STEPS.map((s, i) => {
                const Icon = s.icon;
                const state = i < step ? 'done' : i === step ? 'active' : 'todo';
                return (
                  <li key={s.key} className="flex items-center gap-2 sm:gap-3" data-testid={PLANNER_PAGE.step(i)} data-state={state}>
                    <span
                      className={`flex h-9 w-9 items-center justify-center rounded-full border transition-colors duration-200 ${
                        state === 'active'
                          ? 'border-[hsl(var(--lxra-gold))] bg-[hsl(var(--lxra-gold))] text-[#0A0A0A]'
                          : state === 'done'
                            ? 'border-[hsl(var(--lxra-gold))]/60 text-[hsl(var(--lxra-gold))]'
                            : 'border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-muted))]'
                      }`}
                    >
                      {state === 'done' ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                    </span>
                    <span
                      className={`hidden sm:block text-[11px] uppercase tracking-[0.12em] ${
                        state === 'active' ? 'text-[hsl(var(--lxra-fg))]' : 'text-[hsl(var(--lxra-muted))]'
                      }`}
                    >
                      {s.label}
                    </span>
                    {i < STEPS.length - 1 && <span className="h-px w-4 sm:w-8 bg-[hsl(var(--lxra-border))]" />}
                  </li>
                );
              })}
            </ol>

            <form onSubmit={handleSubmit} noValidate>
              <AnimatePresence mode="wait" custom={direction} initial={false}>
                <motion.div
                  key={step}
                  custom={direction}
                  variants={variants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.26, ease: [0.16, 1, 0.3, 1] }}
                  className="space-y-6"
                >
                  {step === 0 && (
                    <>
                      <div>
                        <p className="text-xs tracking-[0.22em] uppercase text-[hsl(var(--lxra-gold))]">Step 1 of 4</p>
                        <h2 className="font-display text-3xl tracking-[0.03em] text-[hsl(var(--lxra-fg))] mt-1">WHAT ARE WE CREATING?</h2>
                      </div>
                      <div>
                        <Label className={labelClass}>Service</Label>
                        <Select value={form.service_slug || 'none'} onValueChange={(v) => set('service_slug')(v === 'none' ? '' : v)}>
                          <SelectTrigger className={`${fieldClass} h-11`} data-testid={PLANNER_PAGE.serviceSelect}>
                            <SelectValue placeholder="Choose a service (optional)" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]">
                            <SelectItem value="none">Not sure yet</SelectItem>
                            {services.map((s) => (
                              <SelectItem key={s.slug} value={s.slug} data-testid={`planner-service-option-${s.slug}`}>
                                {s.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {selectedService && (
                          <p className="mt-2 text-xs text-[hsl(var(--lxra-muted))]">{selectedService.short_description}</p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="occasion" className={labelClass}>The Occasion</Label>
                        <Input
                          id="occasion"
                          value={form.occasion}
                          onChange={setFromEvent('occasion')}
                          placeholder="Graduation at Georgetown, an engagement, a brand launch..."
                          data-testid={PLANNER_PAGE.occasionInput}
                          className={fieldClass}
                        />
                        {errors.occasion && <p className={errorClass} data-testid="planner-error-occasion" role="alert">{errors.occasion}</p>}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <Label htmlFor="session_date" className={labelClass}>Ideal Date</Label>
                          <Input
                            id="session_date"
                            type="date"
                            value={form.session_date}
                            onChange={setFromEvent('session_date')}
                            data-testid={PLANNER_PAGE.dateInput}
                            className={`${fieldClass} [color-scheme:dark]`}
                          />
                        </div>
                        <div>
                          <Label htmlFor="people_count" className={labelClass}>People in Frame</Label>
                          <Input
                            id="people_count"
                            type="number"
                            min={1}
                            max={200}
                            value={form.people_count}
                            onChange={setFromEvent('people_count')}
                            data-testid={PLANNER_PAGE.peopleInput}
                            className={fieldClass}
                          />
                          {errors.people_count && <p className={errorClass} data-testid="planner-error-people-count" role="alert">{errors.people_count}</p>}
                        </div>
                      </div>
                      <div>
                        <Label className={labelClass}>Preferred Light</Label>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {TIME_OPTIONS.map((t) => (
                            <Pill
                              key={t.value}
                              selected={form.time_of_day === t.value}
                              onClick={() => set('time_of_day')(t.value)}
                              testId={PLANNER_PAGE.timeOption(slugify(t.value))}
                            >
                              {t.label}
                            </Pill>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  {step === 1 && (
                    <>
                      <div>
                        <p className="text-xs tracking-[0.22em] uppercase text-[hsl(var(--lxra-gold))]">Step 2 of 4</p>
                        <h2 className="font-display text-3xl tracking-[0.03em] text-[hsl(var(--lxra-fg))] mt-1">HOW SHOULD IT FEEL?</h2>
                      </div>
                      <div>
                        <Label className={labelClass}>Aesthetic (choose any)</Label>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {AESTHETIC_OPTIONS.map((a) => (
                            <Pill key={a} selected={form.aesthetic.includes(a)} onClick={toggleIn('aesthetic', a)} testId={PLANNER_PAGE.aestheticOption(slugify(a))}>
                              {a}
                            </Pill>
                          ))}
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="location_preference" className={labelClass}>Location Ideas</Label>
                        <Input
                          id="location_preference"
                          value={form.location_preference}
                          onChange={setFromEvent('location_preference')}
                          placeholder="Georgetown waterfront, National Mall, your campus, a studio, or 'surprise me'"
                          data-testid={PLANNER_PAGE.locationInput}
                          className={fieldClass}
                        />
                      </div>
                      <div>
                        <Label htmlFor="outfit_notes" className={labelClass}>Outfits & Styling</Label>
                        <Textarea
                          id="outfit_notes"
                          rows={3}
                          value={form.outfit_notes}
                          onChange={setFromEvent('outfit_notes')}
                          placeholder="Cap and gown plus a second look, matching neutrals, a favorite jacket..."
                          data-testid={PLANNER_PAGE.outfitTextarea}
                          className={`${fieldClass} resize-none`}
                        />
                      </div>
                    </>
                  )}

                  {step === 2 && (
                    <>
                      <div>
                        <p className="text-xs tracking-[0.22em] uppercase text-[hsl(var(--lxra-gold))]">Step 3 of 4</p>
                        <h2 className="font-display text-3xl tracking-[0.03em] text-[hsl(var(--lxra-fg))] mt-1">WHERE WILL THE STORY LIVE?</h2>
                      </div>
                      <div>
                        <Label className={labelClass}>Intended Use (choose any)</Label>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {USE_OPTIONS.map((u) => (
                            <Pill key={u} selected={form.intended_use.includes(u)} onClick={toggleIn('intended_use', u)} testId={PLANNER_PAGE.intendedUseOption(slugify(u))}>
                              {u}
                            </Pill>
                          ))}
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="must_have_shots" className={labelClass}>Must-Have Moments</Label>
                        <Textarea
                          id="must_have_shots"
                          rows={3}
                          value={form.must_have_shots}
                          onChange={setFromEvent('must_have_shots')}
                          placeholder="The cap toss, a candid with parents, the ring detail..."
                          data-testid={PLANNER_PAGE.mustHaveTextarea}
                          className={`${fieldClass} resize-none`}
                        />
                      </div>
                      <div>
                        <Label htmlFor="additional_notes" className={labelClass}>Anything Else</Label>
                        <Textarea
                          id="additional_notes"
                          rows={3}
                          value={form.additional_notes}
                          onChange={setFromEvent('additional_notes')}
                          placeholder="Timing constraints, accessibility needs, references you love..."
                          data-testid={PLANNER_PAGE.notesTextarea}
                          className={`${fieldClass} resize-none`}
                        />
                      </div>
                    </>
                  )}

                  {step === 3 && (
                    <>
                      <div>
                        <p className="text-xs tracking-[0.22em] uppercase text-[hsl(var(--lxra-gold))]">Step 4 of 4</p>
                        <h2 className="font-display text-3xl tracking-[0.03em] text-[hsl(var(--lxra-fg))] mt-1">WHERE SHOULD WE SEND IT?</h2>
                      </div>
                      <div>
                        <Label htmlFor="name" className={labelClass}>Your Name</Label>
                        <Input id="name" value={form.name} onChange={setFromEvent('name')} placeholder="Jane Doe" data-testid={PLANNER_PAGE.nameInput} className={fieldClass} />
                        {errors.name && <p className={errorClass} data-testid="planner-error-name" role="alert">{errors.name}</p>}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <Label htmlFor="email" className={labelClass}>Email</Label>
                          <Input id="email" type="email" value={form.email} onChange={setFromEvent('email')} placeholder="jane@email.com" data-testid={PLANNER_PAGE.emailInput} className={fieldClass} />
                          {errors.email && <p className={errorClass} data-testid="planner-error-email" role="alert">{errors.email}</p>}
                        </div>
                        <div>
                          <Label htmlFor="phone" className={labelClass}>Phone (optional)</Label>
                          <Input id="phone" type="tel" value={form.phone} onChange={setFromEvent('phone')} placeholder="571-000-0000" data-testid={PLANNER_PAGE.phoneInput} className={fieldClass} />
                        </div>
                      </div>
                      <p className="text-xs text-[hsl(var(--lxra-muted))] leading-relaxed">
                        Your blueprint is composed by LXRA Intelligence and reviewed by our photographer. We&apos;ll only use your details to follow up about this session.
                      </p>
                      {errors.submit && (
                        <p className={errorClass} data-testid={PLANNER_PAGE.errorText}>{errors.submit}</p>
                      )}
                    </>
                  )}
                </motion.div>
              </AnimatePresence>

              <div className="mt-10 flex items-center justify-between gap-4">
                {step > 0 ? (
                  <button
                    type="button"
                    onClick={goBack}
                    data-testid={PLANNER_PAGE.backButton}
                    className="inline-flex items-center h-11 px-5 rounded-xl border border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))] transition-colors duration-200 text-xs uppercase tracking-[0.1em] font-medium"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                  </button>
                ) : (
                  <Link to="/services" className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))] transition-colors">
                    Browse services first
                  </Link>
                )}

                {step < STEPS.length - 1 ? (
                  <button
                    type="button"
                    onClick={goNext}
                    data-testid={PLANNER_PAGE.nextButton}
                    className="inline-flex items-center h-12 px-7 rounded-xl bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] hover:-translate-y-0.5 transition-transform duration-200 text-xs uppercase tracking-[0.1em] font-semibold"
                  >
                    Continue
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid={PLANNER_PAGE.submitButton}
                    className="inline-flex items-center h-12 px-7 rounded-xl bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] hover:-translate-y-0.5 transition-transform duration-200 text-xs uppercase tracking-[0.1em] font-semibold disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Composing...
                      </>
                    ) : (
                      <>
                        Compose My Blueprint
                        <Sparkles className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
        )}
      </section>
    </div>
  );
}
